import React from "react";
import { LISTING_TYPES } from "repoV2/constants/listing";
import {
    getRandomInt,
    getRandomIntInRange,
} from "repoV2/features/Common/modules/Random/Random.utils";

// defaults
// popup is displayed for 10 sec
const DEFAULT_POPUP_VISIBLE_DURATION = 10 * 1000;

// timeGap display list
// contains valus for time gap since last purchase in minutes
// reqd. for randomized recent sales data
const DEFAULT_POPUP_TIME_GAP_DISPLAY_LIST = [15, 30, 45, 60];

// gives default popup time config list
// contains startAfterPrevTime
// startAfterPrevTime => start time of current popup display from prev popup display time
const getDefaultPopupTimeConfigList = () => [
    { startAfterPrevTime: 15 * 1000 },
    { startAfterPrevTime: (getRandomInt(3) + 1) * 1000 * 60 },
    { startAfterPrevTime: (getRandomInt(3) + 3) * 1000 * 60 },
];

// min number of cutomers count
const MIN_CUSTOMER_COUNT = 2;

// max number of cutomers count
const MAX_CUSTOMER_COUNT = 10;

export const getRandomStatsForRecentSales = ({
    minCustomers,
    maxCustomers,
    timeGapDisplayList,
}: {
    minCustomers: number;
    maxCustomers: number;
    timeGapDisplayList: Array<number>;
}) => {
    // get a random number from minCustomers to maxCustomers
    const peopleCount = getRandomIntInRange(maxCustomers, minCustomers);

    // get a random number from timeGapDisplayList
    const lastPurchaseTimeGap =
        timeGapDisplayList[getRandomInt(timeGapDisplayList.length)];

    const recentSalesData = {
        peopleCount, // number of people who purchased the listing
        lastPurchaseTimeGap, // time gap since last purchase in minutes
    };
    return recentSalesData;
};

/**
 * @dev
 * @param_summary
 * {
 *  enablePopup => to enable recent sales popup display,
 *  listingType => listing type number,
 *  visibleDuration => time till which current popup displays,
 *  timeGapDisplayList => random list for showing last time purchase,
 *  popUpTimeConfigList =>
 *      contains list of
 *      startAfterPrevTime which is start time of current popup display
 *      from prev popup display time
 *  minCustomers => min number of cutomers count used in popup,
 *  maxCustomers => max number of cutomers count used in popup
 * }
 * @returns checkout recent sales data
 * {
 *    showRecentSalesPopup => show/hide recent sales popup,
 *    recentSalesRef => recent sales data ref,
 *    actionText => action text purchased or booked based on listing type,
 *   }
 */
export const useCheckoutRecentSalesPopup = ({
    enablePopup,
    listingType,
    visibleDuration = DEFAULT_POPUP_VISIBLE_DURATION,
    timeGapDisplayList = DEFAULT_POPUP_TIME_GAP_DISPLAY_LIST,
    popUpTimeConfigList: popUpTimeConfigListProp,
    minCustomers = MIN_CUSTOMER_COUNT,
    maxCustomers = MAX_CUSTOMER_COUNT,
}: {
    enablePopup: boolean;
    listingType: number;
    visibleDuration?: number;
    timeGapDisplayList?: number[];
    popUpTimeConfigList?: Array<{
        startAfterPrevTime: number;
    }>;
    minCustomers?: number;
    maxCustomers?: number;
}) => {
    // creating a state so that, its value is only set once
    const [popUpTimeConfigList] = React.useState(
        popUpTimeConfigListProp || getDefaultPopupTimeConfigList()
    );

    // show/hide sales pop up
    const [recentSalesPopupDetails, setRecentSalesPopupDetails] =
        React.useState<{
            show: boolean;
            peopleCount?: number;
            lastPurchaseTimeGap?: number;
        }>({
            show: false,
            peopleCount: 0,
            lastPurchaseTimeGap: 0,
        });

    // showRecentSalesPopup logic
    React.useEffect(() => {
        if (enablePopup) {
            // get startAfterPrevTimes list for all popups
            const startAfterPrevTimes = popUpTimeConfigList.map(
                ({ startAfterPrevTime }) => startAfterPrevTime
            );
            // add initial start time for popups
            const timesLength = startAfterPrevTimes.unshift(0);

            // create pop up show Queue using these start times
            for (let i = 1; i < timesLength; i += 1) {
                // add
                startAfterPrevTimes[i] += startAfterPrevTimes[i - 1];
                setTimeout(() => {
                    setRecentSalesPopupDetails({
                        show: true,
                        ...getRandomStatsForRecentSales({
                            minCustomers,
                            maxCustomers,
                            timeGapDisplayList,
                        }),
                    });
                }, startAfterPrevTimes[i]);
                startAfterPrevTimes[i] += visibleDuration;
                setTimeout(() => {
                    setRecentSalesPopupDetails({ show: false });
                }, startAfterPrevTimes[i]);
            }
        }
    }, []);

    // get action text based on listing type
    // returns value in ["booked", "purchased"] based on listing type
    const actionText = getActionTextFromListingType({ listingType });

    return {
        ...recentSalesPopupDetails,
        actionText,
    };
};

export const getActionTextFromListingType = ({
    listingType,
}: {
    listingType: number;
}) => {
    let actionText;
    switch (listingType) {
        case LISTING_TYPES.APPOINTMENTS:
        case LISTING_TYPES.WEBINAR:
        case LISTING_TYPES.ONE_ON_ONE_CLASSES:
        case LISTING_TYPES.GROUP_CLASSES:
        case LISTING_TYPES.ROLLING_CLASSES:
        case LISTING_TYPES.WORKSHOPS:
            actionText = "booked";
            break;
        case LISTING_TYPES.NO_SCHEDULE:
        case LISTING_TYPES.BRANDED_COMMUNITY:
        case LISTING_TYPES.WHATSAPP:
        case LISTING_TYPES.TELEGRAM:
        case LISTING_TYPES.RECORDED_CONTENT:
        case LISTING_TYPES.MERCHANDISE:
            actionText = "purchased";
            break;
        default:
            actionText = "purchased";
            break;
    }
    return actionText;
};
